import Vue from 'vue';
import NetworkLayout from 'src/components/project/analysis/results/widgets/NetworkLayout.vue';
import { mapGetters, mapActions } from 'vuex';
import { LOAD_SAVED_QUERIES } from 'src/store/types';
import DownloadExportButton from './widgets/DownloadExportButton.vue';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
import Excerpts from './widgets/Excerpts.vue';
import ExcerptsUnmapped from './widgets/ExcerptsUnmapped.vue';
import PageLoader from 'src/components/widgets/PageLoader.vue';
import Utils from 'src/utils/general';
import { hasUnstructured, expandQuery } from 'src/utils/query';
import Query, { expandSavedQueryIfPossible } from 'src/api/query';
import { EmergentConcepts } from 'src/components/DataWidgets';
import FormatUtils from 'src/utils/formatters';
const MIN_CONCEPTS = 3; // minimum number of concepts to render the network
const NUM_NETWORK_CONCEPTS = 50; // number of concepts to show
export default Vue.extend({
    components: {
        DownloadExportButton,
        Dropdown,
        DropdownItem,
        Excerpts,
        ExcerptsUnmapped,
        NetworkLayout,
        PageLoader,
        EmergentConcepts
    },
    props: {
        analysisId: { type: Number, required: true },
        projectId: { type: Number, required: true },
        navigatedConcept: { type: String, required: false, default: null },
    },
    data() {
        return {
            isLoading: true,
            isNetworkDrawn: false,
            model: undefined,
            numConceptsShown: 10,
            totalHits: undefined,
            selectedConcept: undefined,
            hits: {},
        };
    },
    computed: {
        ...mapGetters([
            'currentProject', 'currentAnalysis', 'currentModel', 'currentSite',
            'savedQueries', 'featureFlags', 'defaultDateField', 'dateFields',
            'widgetBanners'
        ]),
        hasDate() {
            return this.dateFields.length > 0;
        },
        fractionUnmapped() {
            if (this.isLoading
                || this.isInsufficientData
                || this.model?.n_frames === undefined
                || this.currentModel?.stats?.non_empty_frames === undefined) {
                return NaN;
            }
            return this.model.n_frames / this.currentModel.stats.non_empty_frames;
        },
        isInsufficientData() {
            return this.model && this.model.concepts.length < MIN_CONCEPTS;
        },
        maxConceptFrequency() {
            return this.sortedConcepts[0][1];
        },
        savedQueriesWithText() {
            return this.savedQueries.filter(({ name, query_value }) => hasUnstructured([expandQuery(name, query_value, this.savedQueries)]));
        },
        // Other (unmapped verbatims)
        otherQuery() {
            const q = {
                type: 'match_all',
                includes: [{ type: 'all_data' }],
                excludes: this.savedQueriesWithText.map(query => query.query_value)
            };
            if (this.selectedConcept) {
                q.includes = [{ type: 'text', value: this.selectedConcept }];
            }
            return q;
        },
        excludeQueriesList() {
            const list = this.savedQueriesWithText
                .map((q) => {
                return {
                    name: q.name,
                    value: expandSavedQueryIfPossible(q.name, q.query_value, this.savedQueries)
                };
            });
            return list;
        },
        baseQuery() {
            // This query is the base query that provides the result for all non-empty
            // verbatims. In case a concept is selected, this query morphs to search for
            // all instances of that particular concept.
            const q = {
                type: 'match_all',
                includes: [
                    {
                        type: 'nonempty_data'
                    }
                ]
            };
            if (this.selectedConcept) {
                q.includes = [{ type: 'text', value: this.selectedConcept }];
            }
            return q;
        },
        unmappedBody() {
            // The excludeQueriesList and BaseQuery combined to create the request body for
            // unmapped screen. Mainly used for the UnmappedExcerpts widget
            return { exclude_queries_list: this.excludeQueriesList, base_query: this.baseQuery };
        },
        emergentConceptQuery() {
            if (this.savedQueriesWithText.length == 0) {
                return [{
                        type: 'match_all',
                        includes: this.selectedConcept
                            ? [{ type: 'text', value: this.selectedConcept }]
                            : [{ type: 'all_data' }],
                        excludes: []
                    }];
            }
            else {
                return this.savedQueriesWithText.map(query => {
                    return {
                        type: 'match_all',
                        includes: this.selectedConcept
                            ? [{ type: 'text', value: this.selectedConcept }]
                            : [{ type: 'all_data' }],
                        excludes: [query.query_value]
                    };
                });
            }
        },
        // Concepts sorted by frequency decreasing
        sortedConcepts() {
            const concepts = [];
            for (const concept of this.model.concepts) {
                concepts.push([concept.name, concept.frequency]);
            }
            concepts.sort((v1, v2) => v2[1] - v1[1]);
            return concepts;
        },
        // Concepts sorted alphanumerically
        sortedConceptsAlpha() {
            const concepts = [];
            for (const concept of this.model.concepts) {
                concepts.push(concept.name);
            }
            concepts.sort(Utils.naturalSort({ caseSensitive: false }));
            return concepts;
        }
    },
    watch: {
        savedQueriesWithText() {
            this.generateModel();
        },
        isLoading(newVal) {
            if (!newVal) {
                setTimeout(() => {
                    if (this.navigatedConcept) {
                        this.queryConcept(this.navigatedConcept, true);
                    }
                }, 1000);
            }
        },
    },
    async mounted() {
        await this.LOAD_SAVED_QUERIES({ projectId: this.projectId, analysisId: this.analysisId });
    },
    methods: {
        ...mapActions({ LOAD_SAVED_QUERIES }),
        number: FormatUtils.number,
        decimalAsPercent: FormatUtils.decimalAsPercent,
        // Make API call to generate the model
        generateModel() {
            this.generateModelQeiii();
            // Track usage of the page
            this.$analytics.track.analysis.viewUnmapped(this.savedQueriesWithText.length, this.totalHits, this.fractionUnmapped);
        },
        generateModelQeiii() {
            Query.runUnmapped(this.projectId, this.unmappedBody, this.currentAnalysis.topic_framework_id, this.currentSite.chrysalis_url, this.currentProject.chrysalis_ref, {
                networkModel: true,
                numNetworkConcepts: NUM_NETWORK_CONCEPTS,
            }).then(result => {
                this.model = result.model;
                this.hits = result.hits;
                if (this.model && !this.model.concepts) {
                    // Legacy network model support
                    this.model.concepts = this.model.terms;
                    this.model.links = this.model.npmi;
                    this.model.driving_concepts = [];
                }
                this.totalHits = result.total_hits;
                this.isLoading = false;
            });
        },
        // Generate query link for concept
        generateQueryConceptLink(concept) {
            const query = [{
                    type: 'text',
                    values: [concept],
                    operator: 'includes'
                }];
            return Utils.generateQueryLink(query);
        },
        // Returns a fully resolved query link for concept, rather than a route object
        generateResolvedQueryConceptLink(concept) {
            const route = this.generateQueryConceptLink(concept);
            route.params = {
                projectId: this.projectId,
                analysisId: this.analysisId
            };
            return this.$router.resolve(route).href;
        },
        getUnmappedTermsCsvData() {
            return this.sortedConcepts.map((concept) => {
                return {
                    name: concept[0],
                    frequency: concept[1],
                    coverage: concept[1] / this.model.n_frames
                };
            });
        },
        getChartEl() {
            return this.$el.querySelector('div.concepts-chart');
        },
        getExportConfig() {
            return {
                dims: this.getChartEl().getBoundingClientRect(),
                css: `
          text {
            color: #383838;
            font-size: 14px;
            stroke: none;
          }
          .line {
            stroke-width: 2px;
          }
          .axis path, .axis line {
            shape-rendering: crispEdges;
            stroke: #ebebeb;
            stroke-width: 2px;
            opacity: 0.5;
          }
        `
            };
        },
        // Query a concept; called based on click-interaction.
        queryConcept(concept, scrollToVerbatims = false) {
            if (scrollToVerbatims) {
                this.$refs.verbatims.scrollIntoView();
            }
            this.selectedConcept = concept;
        }
    }
});
